import { useId } from 'react'
import { Link, useNavigate } from 'react-router'
import { cx } from '../../ui'

/**
 * Settings, split by who a setting affects (owner, 2026-09-25): the workspace itself, the Microsoft
 * tenant it reads, the people in it, the emergency and service accounts the checks accept, and you.
 *
 * It was one long page of eight cards with a row of jump links, and an admin looking for "invite"
 * scrolled past five of them. Each tab has its own address (/settings/team), so a link, a bookmark,
 * a refresh and the back button all land on the same tab.
 */
export type SettingsTab = 'workspace' | 'tenant' | 'azure' | 'ai' | 'team' | 'emergency-accounts' | 'account'

/**
 * Whose setting a page holds (owner approved, 2026-10-11): the workspace's, which everyone in it
 * shares; what Defenssive connects to and reads; and the signed-in person's own. The menu groups its
 * pages under these, the way a settings menu says whether a setting is personal or the workspace's.
 */
export type SettingsGroup = 'workspace' | 'connections' | 'personal'

export const SETTINGS_GROUPS: readonly { id: SettingsGroup; label: string }[] = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'connections', label: 'Connections' },
  { id: 'personal', label: 'Personal' },
]

type TabEntry = { id: SettingsTab; label: string; group: SettingsGroup }

export const SETTINGS_TABS: readonly TabEntry[] = [
  // "General" under the Workspace heading: the page keeps its address (/settings/workspace) and its
  // first part is still headed "Workspace".
  { id: 'workspace', label: 'General', group: 'workspace' },
  { id: 'team', label: 'Team', group: 'workspace' },
  // The finding steps say "Settings > Emergency and service accounts"; the tab keeps the first word
  // and the card inside it carries the whole name.
  { id: 'emergency-accounts', label: 'Emergency accounts', group: 'workspace' },
  { id: 'tenant', label: 'Microsoft tenant', group: 'connections' },
  { id: 'account', label: 'Your account', group: 'personal' },
]

/**
 * Azure, shown only where Azure assessment is switched on (the server answers /api/workspace/azure),
 * after the Microsoft tenant: both are what Defenssive reads.
 */
export const AZURE_TAB: TabEntry = { id: 'azure', label: 'Azure', group: 'connections' }

/** AI (bring your own Azure OpenAI), shown only where it is switched on (/api/workspace/ai answers), after Azure. */
export const AI_TAB: TabEntry = { id: 'ai', label: 'AI', group: 'connections' }

function tabsFor(withAzure: boolean, withAi = false): readonly TabEntry[] {
  const at = SETTINGS_TABS.findIndex((t) => t.id === 'tenant') + 1
  const extra = [...(withAzure ? [AZURE_TAB] : []), ...(withAi ? [AI_TAB] : [])]
  if (extra.length === 0) return SETTINGS_TABS
  return [...SETTINGS_TABS.slice(0, at), ...extra, ...SETTINGS_TABS.slice(at)]
}

/** Where each tab lives. */
export const settingsPath = (tab: SettingsTab) => `/settings/${tab}`

/**
 * The old single page's jump links, so a saved /settings#settings-invite still lands on the part it
 * named rather than on the first tab.
 */
const LEGACY_ANCHORS: Record<string, SettingsTab> = {
  '#settings-account': 'account',
  '#settings-workspace': 'workspace',
  '#settings-tenant': 'tenant',
  '#settings-notifications': 'account',
  '#settings-members': 'team',
  '#settings-invite': 'team',
  '#settings-exempt': 'emergency-accounts',
  '#settings-close': 'workspace',
}

/**
 * The tab an address names. /settings/{tab} first; then an old jump link; then Microsoft's sign-in
 * callback, which returns to /settings?linked=... and whose result is shown on Your account; else
 * Workspace. An unknown tab is Workspace rather than an empty page.
 */
export function settingsTabFor(pathname: string, search = '', hash = ''): SettingsTab {
  const segment = pathname.replace(/\/+$/, '').split('/')[2]
  const named = tabsFor(true, true).find((t) => t.id === segment)
  if (named) return named.id

  const legacy = LEGACY_ANCHORS[hash]
  if (legacy) return legacy

  if (new URLSearchParams(search).has('linked')) return 'account'

  return 'workspace'
}

/**
 * Tabs that are links: each one is a page with its own address, so this is navigation (the current
 * one marked aria-current="page"), not an in-page tablist that arrow keys would move through.
 *
 * A menu down the side, its pages grouped under Workspace, Connections and Personal (each group named
 * to a screen reader). On a narrow screen a grouped native picker exposes every available section
 * without hiding the later pages off the side of the screen. Both use the same routes and gates.
 */
export function SettingsTabs({ current, withAzure = false, withAi = false }: { current: SettingsTab; withAzure?: boolean; withAi?: boolean }) {
  const navigate = useNavigate()
  const groupId = useId()
  const tabs = tabsFor(withAzure, withAi)

  return (
    <nav className="settings-tabs" aria-label="Settings sections">
      <div className="settings-picker">
        <label htmlFor={`${groupId}-picker`}>Settings section</label>
        <select
          id={`${groupId}-picker`}
          value={current}
          onChange={(event) => {
            const next = tabs.find((tab) => tab.id === event.currentTarget.value)
            if (next) navigate(settingsPath(next.id))
          }}
        >
          {SETTINGS_GROUPS.map((group) => (
            <optgroup key={group.id} label={group.label}>
              {tabs.filter((tab) => tab.group === group.id).map((tab) => (
                <option key={tab.id} value={tab.id}>{tab.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <div className="settings-tabs-list">
      {SETTINGS_GROUPS.map((group) => (
        <div
          key={group.id}
          className="settings-tab-group"
          role="group"
          aria-labelledby={`${groupId}-${group.id}`}
        >
          <span id={`${groupId}-${group.id}`} className="settings-tab-group-label">{group.label}</span>
          {tabs.filter((tab) => tab.group === group.id).map((tab) => (
            <Link
              key={tab.id}
              to={settingsPath(tab.id)}
              className={cx('settings-tab', tab.id === current && 'is-current')}
              aria-current={tab.id === current ? 'page' : undefined}
            >
              {tab.label}
            </Link>
          ))}
        </div>
      ))}
      </div>
    </nav>
  )
}
